import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Spacer } from "../../styles/CommonElements";

const renderInline = (text) => {
  const parts = [];
  const pattern = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let match = pattern.exec(text);

  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];
    if (token.startsWith("**")) {
      parts.push(<strong key={`${match.index}-bold`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("[")) {
      const linkMatch = /\[([^\]]+)\]\(([^)]+)\)/.exec(token);
      if (linkMatch) {
        const [, label, href] = linkMatch;
        if (href.startsWith("/")) {
          parts.push(
            <Link key={`${match.index}-link`} to={href}>
              {label}
            </Link>
          );
        } else {
          parts.push(
            <a key={`${match.index}-link`} href={href} rel="noreferrer" target="_blank">
              {label}
            </a>
          );
        }
      }
    }

    lastIndex = match.index + token.length;
    match = pattern.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

const renderMarkdown = (markdown) => {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const elements = [];
  let listItems = [];
  let inTable = false;
  let tableRows = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    elements.push(
      <List key={`list-${elements.length}`}>
        {listItems.map((item, index) => (
          <li key={index}>{renderInline(item)}</li>
        ))}
      </List>
    );
    listItems = [];
  };

  const flushTable = () => {
    if (tableRows.length === 0) return;
    const [headerRow, , ...bodyRows] = tableRows;
    const headers = headerRow
      .split("|")
      .filter(Boolean)
      .map((cell) => cell.trim());

    elements.push(
      <Table key={`table-${elements.length}`}>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{renderInline(header)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, rowIndex) => {
            const cells = row
              .split("|")
              .filter(Boolean)
              .map((cell) => cell.trim());
            return (
              <tr key={rowIndex}>
                {cells.map((cell, cellIndex) => (
                  <td key={cellIndex}>{renderInline(cell)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
    );

    tableRows = [];
    inTable = false;
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (trimmed.startsWith("|")) {
      flushList();
      inTable = true;
      tableRows.push(trimmed);
      return;
    }

    if (inTable) {
      flushTable();
    }

    if (trimmed === "") {
      flushList();
      return;
    }

    if (trimmed === "---") {
      flushList();
      elements.push(<Divider key={`hr-${index}`} />);
      return;
    }

    if (trimmed.startsWith("### ")) {
      flushList();
      elements.push(<H3 key={`h3-${index}`}>{renderInline(trimmed.slice(4))}</H3>);
      return;
    }

    if (trimmed.startsWith("## ")) {
      flushList();
      elements.push(<H2 key={`h2-${index}`}>{renderInline(trimmed.slice(3))}</H2>);
      return;
    }

    if (trimmed.startsWith("# ")) {
      flushList();
      elements.push(<H1 key={`h1-${index}`}>{renderInline(trimmed.slice(2))}</H1>);
      return;
    }

    if (trimmed.startsWith("- ")) {
      listItems.push(trimmed.slice(2));
      return;
    }

    flushList();
    elements.push(<Paragraph key={`p-${index}`}>{renderInline(trimmed)}</Paragraph>);
  });

  flushList();
  if (inTable) flushTable();

  return elements;
};

export default function LegalMarkdownPage({ title, documentPath, mustAcceptTos = false }) {
  const [content, setContent] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadDocument = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(documentPath);
        if (!response.ok) {
          throw new Error(`Failed to load legal document (${response.status})`);
        }
        const text = await response.text();
        if (!cancelled) {
          setContent(text);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unable to load document");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadDocument();

    return () => {
      cancelled = true;
    };
  }, [documentPath]);

  return (
    <Page $mustAcceptTos={mustAcceptTos}>
      <MainHeader>{title}</MainHeader>
      <Spacer $marginTop="1.5rem" />
      <TextContainer $mustAcceptTos={mustAcceptTos}>
        {loading && <StatusText>Loading…</StatusText>}
        {error && <StatusText $error>{error}</StatusText>}
        {!loading && !error && renderMarkdown(content)}
      </TextContainer>
    </Page>
  );
}

const Page = styled.div`
  color: #0f172a;
  font-size: 0.9375rem;
  line-height: 1.65;
  text-align: left;
  width: min(56rem, 92%);
  max-width: 56rem;
  margin: 3rem auto 2.5rem;

  a {
    color: #0d9488;
    text-decoration: underline;
  }
`;

const TextContainer = styled.div`
  text-align: left;
  overflow-y: ${(p) => (p.$mustAcceptTos ? "auto" : "visible")};
  max-height: ${(p) => (p.$mustAcceptTos ? "500px" : "none")};
  padding-right: 4%;
  padding-bottom: 2rem;
`;

const MainHeader = styled.h1`
  text-align: center;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  color: #0f172a;
`;

const H1 = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 1rem;
  color: #0f172a;
  text-align: left;
`;

const H2 = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 2rem 0 0.75rem;
  color: #0f172a;
  text-align: left;
`;

const H3 = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 1.25rem 0 0.5rem;
  color: #1e293b;
  text-align: left;
`;

const Paragraph = styled.p`
  margin: 0.75rem 0;
  color: #334155;
  text-align: left;
`;

const List = styled.ul`
  margin: 0.75rem 0 0.75rem 1.25rem;
  padding-left: 1.25rem;
  color: #334155;
  text-align: left;
  list-style-position: outside;

  li + li {
    margin-top: 0.4rem;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e2e8f0;
  margin: 1.5rem 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.875rem;

  th,
  td {
    border: 1px solid #e2e8f0;
    padding: 0.625rem 0.75rem;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #f8fafc;
    font-weight: 600;
  }
`;

const StatusText = styled.p`
  color: ${(p) => (p.$error ? "#dc2626" : "#64748b")};
  margin: 0;
  text-align: left;
`;
